"use client";

import i18next from "i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import { createContext, useContext, useEffect, useState } from "react";
import {
  UseTranslationOptions,
  initReactI18next,
  useTranslation as useTranslationOriginal,
} from "react-i18next";

import { fallbackLng, getOptions, languages } from "./settings";

const runsOnServerSide = typeof window === "undefined";

export const LocaleContext = createContext(fallbackLng);

// A tab left open across a deploy can request a namespace whose hashed file no
// longer exists. TanStack Router reloads for missing route chunks, but these
// imports bypass it, so reload once ourselves. The sessionStorage guard (same
// approach as TanStack's) prevents a reload loop if the file is really gone.
function reloadOnceForStaleChunk(error: Error): never {
  const storageKey = `i18n_reload:${error.message}`;
  if (!runsOnServerSide && !sessionStorage.getItem(storageKey)) {
    sessionStorage.setItem(storageKey, "1");
    window.location.reload();
  }
  throw error;
}

i18next
  .use(initReactI18next)
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`../locales/${language}/${namespace}.json`).catch(
          reloadOnceForStaleChunk,
        ),
    ),
  )
  .init({
    ...getOptions(),
    lng: undefined, // let detect the language on client side
    detection: {
      order: ["path", "htmlTag", "cookie", "navigator"],
    },
    preload: languages,
    ns: [
      "common",
      "datetime",
      "validations",
      "pages",
      "PageHeader",
      "GlobalSearch",
      "RespondentEditor",
    ],
  });

export function initLocale(locale: string) {
  if (i18next.resolvedLanguage !== locale) {
    i18next.changeLanguage(locale);
  }
}

export function useTranslation(
  ns: string | string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseTranslationOptions<any>,
) {
  const lng = useContext(LocaleContext);

  const ret = useTranslationOriginal(ns, options);
  const { i18n } = ret;

  if (runsOnServerSide && lng && i18n.resolvedLanguage !== lng) {
    i18n.changeLanguage(lng);
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [activeLng, setActiveLng] = useState(i18n.resolvedLanguage);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (activeLng === i18n.resolvedLanguage) return;
      setActiveLng(i18n.resolvedLanguage);
    }, [activeLng, i18n.resolvedLanguage]);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (!lng || i18n.resolvedLanguage === lng) return;
      i18n.changeLanguage(lng);
    }, [lng, i18n]);
  }
  return ret;
}
